import { find, get } from "lodash";
import * as Yup from "yup";

import { CONTACT_POINT_SYSTEM, POLYNESIA_PHONE_PREFIX } from "utils/constants";

export const DEFAULT_PHONE_PREFIX = "+33";

export const LOCAL_PHONE_INPUT_PATTERN = /^0?[0-9]{9}$/;

export const PHONE_PREFIX_NATIONAL_RULES = {
  "+33": {
    nationalPattern: /^[1-9][0-9]{8}$/,
    examples: ["0610203040", "610203040"],
  },
  "+590": {
    // Landline 590/591, mobile 690/691, toll-free 800-805, premium 810-829 & 890-899
    nationalPattern:
      /^(590|591|690|691|80[0-5]|81[0-9]|82[0-9]|89[0-9])[0-9]{6}$/,
    examples: [
      "0590271234",
      "590271234",
      "0690123456",
      "690123456",
      "0691123456",
      "0800123456",
      "891234567",
    ],
  },
  "+594": {
    // Guyane française (+594, not Guyana +592): landline 594, mobile 694/7093x,
    // toll-free 800-805, premium 810-829 & 890-899
    nationalPattern:
      /^(594[0-9]{6}|694[0-9]{6}|7093[0-3][0-9]{5}|80[0-5][0-9]{6}|81[0-9]{7}|82[0-9]{7}|89[0-9]{7})$/,
    examples: [
      "0594123456",
      "594123456",
      "0694123456",
      "694123456",
      "0800123456",
      "890123456",
    ],
  },
  "+596": {
    // Landline 596, mobile 696/697, toll-free 800 (0800), premium 890 (0890)
    nationalPattern: /^(596|696|697|800|890)[0-9]{6}$/,
    examples: [
      "0696123456",
      "696123456",
      "0596123456",
      "596123456",
      "0800123456",
      "0890123456",
    ],
  },
  "+262": {
    // La Réunion: landline 262/263, mobile 692/693.
    // Mayotte shares +262 (269/639) and is not accepted here.
    nationalPattern: /^(262|263|692|693)[0-9]{6}$/,
    examples: [
      "0262123456",
      "262123456",
      "0263123456",
      "0692123456",
      "692123456",
      "0693123456",
    ],
  },
  [POLYNESIA_PHONE_PREFIX]: {
    // Local numbers are 8 digits starting with 87, 88 or 89 (no leading 0)
    nationalPattern: /^(87|88|89)\d{6}$/,
    localInputPattern: /^0?(87|88|89)\d{6}$/,
    localUsesLeadingZero: false,
    examples: ["87123456", "88123456", "89123456", "087123456"],
  },
};

export const LOCAL_PHONE_MAX_LENGTH = 10;

const toLocalInput = (national, prefix) => {
  if (!national) {
    return "";
  }
  const rule = PHONE_PREFIX_NATIONAL_RULES[prefix];
  if (rule?.localUsesLeadingZero === false) {
    return national.replace(/^0/, "");
  }
  return national.startsWith("0") ? national : `0${national}`;
};

export const normalizeLocalPhoneDigits = (
  value,
  prefix = DEFAULT_PHONE_PREFIX,
) => {
  const cleaned = String(value || "").replace(/\s/g, "");
  const rule = PHONE_PREFIX_NATIONAL_RULES[prefix];
  const inputPattern = rule?.localInputPattern || LOCAL_PHONE_INPUT_PATTERN;
  if (!inputPattern.test(cleaned)) {
    return null;
  }
  if (rule?.localUsesLeadingZero === false) {
    return cleaned.replace(/^0/, "");
  }
  return cleaned.startsWith("0") ? cleaned.slice(1) : cleaned;
};

const MARTINIQUE_SPECIAL_SERVICE_PATTERN = /^0(800|890)[0-9]{6}$/;

export const formatPhoneNumber = (
  phone,
  phonePrefix = DEFAULT_PHONE_PREFIX,
) => {
  if (!phone) {
    return "";
  }

  const cleaned = String(phone).replace(/\s/g, "");
  const prefix = phonePrefix || DEFAULT_PHONE_PREFIX;

  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  if (cleaned.startsWith("00")) {
    return `+${cleaned.slice(2).replace(/\D/g, "")}`;
  }

  const digits = cleaned.replace(/\D/g, "");
  if (!digits) {
    return "";
  }

  const withLeadingZero = digits.startsWith("0") ? digits : `0${digits}`;

  if (
    prefix === "+596" &&
    MARTINIQUE_SPECIAL_SERVICE_PATTERN.test(withLeadingZero)
  ) {
    return `${prefix}${withLeadingZero}`;
  }

  const national = digits.startsWith("0") ? digits.slice(1) : digits;
  return `${prefix}${national}`;
};

export const formatPhoneE164 = (
  prefix = DEFAULT_PHONE_PREFIX,
  localNumber = "",
) => formatPhoneNumber(localNumber, prefix);

/** Display only: prefix when present, otherwise keep the stored number as-is. */
export const getPhoneFromTelecoms = (
  telecoms = [],
  system = CONTACT_POINT_SYSTEM.PHONE,
) => {
  const phone = get(find(telecoms, { system }), "value", "");
  if (!phone) {
    return "";
  }

  if (system !== CONTACT_POINT_SYSTEM.PHONE) {
    return phone;
  }

  const phonePrefix = get(
    find(telecoms, {
      system: CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX,
    }),
    "value",
  );
  if (!phonePrefix) {
    return phone;
  }

  return formatPhoneNumber(phone, phonePrefix);
};

const getPrefixValues = (prefixOptions = []) =>
  prefixOptions.map(({ value }) => value).sort((a, b) => b.length - a.length);

export const parsePhoneValue = (
  storedValue,
  defaultPrefix = DEFAULT_PHONE_PREFIX,
  prefixOptions = [],
) => {
  const cleaned = String(storedValue || "").replace(/\s/g, "");
  if (!cleaned) {
    return { prefix: defaultPrefix, localNumber: "" };
  }

  const prefixValues = getPrefixValues(prefixOptions);
  const matchedPrefix = prefixValues.find((prefix) =>
    cleaned.startsWith(prefix),
  );
  if (matchedPrefix) {
    const national = cleaned.slice(matchedPrefix.length);
    return {
      prefix: matchedPrefix,
      localNumber: toLocalInput(national, matchedPrefix),
    };
  }

  if (LOCAL_PHONE_INPUT_PATTERN.test(cleaned)) {
    return {
      prefix: defaultPrefix,
      localNumber: cleaned.startsWith("0") ? cleaned : `0${cleaned}`,
    };
  }

  const polynesiaRule = PHONE_PREFIX_NATIONAL_RULES[POLYNESIA_PHONE_PREFIX];
  const polynesiaDigits = cleaned.replace(/^0/, "");
  if (polynesiaRule?.nationalPattern.test(polynesiaDigits)) {
    return {
      prefix: POLYNESIA_PHONE_PREFIX,
      localNumber: polynesiaDigits,
    };
  }

  return { prefix: defaultPrefix, localNumber: cleaned };
};

export const resolvePhoneFields = ({
  phoneValue,
  prefixValue,
  prefixOptions = [],
  defaultPrefix = DEFAULT_PHONE_PREFIX,
}) => {
  const cleaned = String(phoneValue || "").replace(/\s/g, "");
  if (cleaned.startsWith("+") || cleaned.startsWith("00")) {
    return parsePhoneValue(phoneValue, defaultPrefix, prefixOptions);
  }

  if (prefixValue) {
    return {
      prefix: prefixValue,
      localNumber: cleaned,
    };
  }

  return parsePhoneValue(phoneValue, defaultPrefix, prefixOptions);
};

export const isValidLocalPhoneNumber = (
  value,
  prefix = DEFAULT_PHONE_PREFIX,
) => {
  const national = normalizeLocalPhoneDigits(value, prefix);
  if (!national) {
    return false;
  }

  const rule = PHONE_PREFIX_NATIONAL_RULES[prefix];
  if (!rule) {
    return true;
  }

  return rule.nationalPattern.test(national);
};

export const getPhonePrefixFromYupContext = (
  context,
  defaultPrefix = DEFAULT_PHONE_PREFIX,
) => {
  const ancestors = context.from || [];

  const resolvePrefixFromNode = (node) => {
    if (!node || typeof node !== "object") {
      return null;
    }

    return (
      get(node, `${CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX}.value`) ||
      get(node, "phoneNumberPrefix.value") ||
      get(node, "phonePrefix") ||
      get(node, `telecoms.${CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX}.value`) ||
      get(
        node,
        `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX}.value`,
      ) ||
      null
    );
  };

  const root = ancestors[ancestors.length - 1]?.value;
  const rootPrefix = resolvePrefixFromNode(root);
  if (rootPrefix) {
    return rootPrefix;
  }

  for (let i = 0; i < ancestors.length; i += 1) {
    const prefix = resolvePrefixFromNode(ancestors[i]?.value);
    if (prefix) {
      return prefix;
    }
  }

  return defaultPrefix;
};

export const getStoredPhonePrefix = (telecoms = []) =>
  String(
    get(
      find(telecoms, {
        system: CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX,
      }),
      "value",
      "",
    ) || "",
  ).trim();

export const hasStoredPhonePrefix = (telecoms = []) =>
  Boolean(getStoredPhonePrefix(telecoms));

export const getEditablePhoneFields = (
  telecoms = [],
  prefixOptions = [],
  defaultPrefix = DEFAULT_PHONE_PREFIX,
) => {
  const phoneValue = String(
    get(find(telecoms, { system: CONTACT_POINT_SYSTEM.PHONE }), "value", "") ||
      "",
  ).replace(/\s/g, "");
  const prefixValue = getStoredPhonePrefix(telecoms);

  if (!prefixValue) {
    return {
      prefix: "",
      localNumber: phoneValue,
      hasStoredPrefix: false,
    };
  }

  return {
    ...resolvePhoneFields({
      phoneValue,
      prefixValue,
      prefixOptions,
      defaultPrefix,
    }),
    hasStoredPrefix: true,
  };
};

export const isValidUnprefixedClientPhone = (value) => {
  const cleaned = String(value || "").replace(/\s+/g, "");
  if (!cleaned) {
    return false;
  }

  if (isValidLocalPhoneNumber(cleaned, POLYNESIA_PHONE_PREFIX)) {
    return true;
  }

  if (cleaned.startsWith("+") || cleaned.startsWith("00")) {
    const knownPrefixes = Object.keys(PHONE_PREFIX_NATIONAL_RULES).map(
      (value) => ({ value, label: value }),
    );
    const { prefix, localNumber } = parsePhoneValue(
      cleaned,
      DEFAULT_PHONE_PREFIX,
      knownPrefixes,
    );
    return isValidLocalPhoneNumber(localNumber, prefix);
  }

  return isValidLocalPhoneNumber(cleaned, DEFAULT_PHONE_PREFIX);
};

export const isValidClientPhoneInput = (
  value,
  prefix = DEFAULT_PHONE_PREFIX,
) => {
  const cleaned = String(value || "").replace(/\s+/g, "");
  if (!cleaned) {
    return false;
  }

  if (cleaned.startsWith("+") || cleaned.startsWith("00")) {
    return isValidUnprefixedClientPhone(cleaned);
  }

  if (prefix) {
    return isValidLocalPhoneNumber(cleaned, prefix);
  }

  return isValidUnprefixedClientPhone(cleaned);
};

export const resolveClientPhoneInput = ({
  phone,
  phonePrefix,
  prefixOptions = [],
  defaultPrefix = DEFAULT_PHONE_PREFIX,
}) => {
  const cleaned = String(phone || "").replace(/\s+/g, "");
  if (cleaned.startsWith("+") || cleaned.startsWith("00")) {
    return parsePhoneValue(
      cleaned,
      phonePrefix || defaultPrefix,
      prefixOptions,
    );
  }

  return {
    prefix: phonePrefix || "",
    localNumber: cleaned,
  };
};

export const createClientPhoneSchema = (messages) =>
  Yup.object({
    phonePrefix: Yup.string().nullable(),
    phone: Yup.string()
      .transform((value) => String(value || "").replace(/\s+/g, ""))
      .required(messages.REQUIRED_PHONE)
      .test("isValid", messages.INVALID_PHONE, function (value) {
        return isValidClientPhoneInput(value, this.parent.phonePrefix);
      }),
  });

export const buildPhoneTelecomsUpdate = ({
  telecoms = [],
  localNumber,
  prefix,
  createId,
}) => {
  const existingPhone = find(telecoms, {
    system: CONTACT_POINT_SYSTEM.PHONE,
  });
  const existingPrefix = find(telecoms, {
    system: CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX,
  });
  const nextTelecoms = [
    ...telecoms.filter(
      (telecom) =>
        telecom.system !== CONTACT_POINT_SYSTEM.PHONE &&
        telecom.system !== CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX,
    ),
    {
      id: get(existingPhone, "id") || createId(),
      system: CONTACT_POINT_SYSTEM.PHONE,
      value: localNumber,
    },
  ];

  if (!prefix) {
    return nextTelecoms;
  }

  return [
    ...nextTelecoms,
    {
      id: get(existingPrefix, "id") || createId(),
      system: CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX,
      value: prefix,
    },
  ];
};
